import React from "react";
import { memo, useMemo, useState, useRef, useCallback, useEffect } from "react";
import clsx from "clsx";
import {
  format,
  hoursToMilliseconds,
  minutesToMilliseconds,
  startOfDay,
  endOfDay,
  startOfToday,
  startOfTomorrow,
  startOfHour,
  setHours,
  subHours,
} from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";
import { Timeline, SHORT_TIMELINE_SHIFT_MS } from "./Timeline.tsx";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import useTimedRender from "../../hooks/useTimedRender.ts";
import parseTimelineValue from "../../utils/dateTime/parseTimelineValue.ts";
// Removed normalizeSeekPos as it's not needed
import mapNumber from "../../utils/mapNumber.ts";
import { DATE_TIME_FORMAT } from "../../utils/dateTime/const.ts";
import { TimelineProps } from "./Timeline.types";
// @ts-ignore
import styles from "./Timeline.module.scss";
import { useMediaQuery } from "@mui/material";

const TIME_LABEL_WIDTH = 130;

export const TimelineWrapper = memo(
  ({
    timeline,
    timeZone,
    currentSeekTime,
    seekPosRef,
    short,
    posOn = true,
    manualPosition,
    onSeek,
    onSetManual,
    timelineItemRef,
    cameraId,
    playbackOn,
    archiveItem = false,
    timelineTransactions,
    fetchTimelineTransactions,
    resetTimelineTransactions,
    setCurrentTimestamp,
  }: TimelineProps) => {
    const [hoverPosition, setHoverPosition] = useState<number | null>(null);
    const [hoverTime, setHoverTime] = useState<number | null>(null);
    const isMobile = useMediaQuery("(max-width: 1000px)");
    const TIME_LABEL_WIDTH = 130;
    const TOOLTIP_FORMAT = "dd/MM/yyyy HH:mm:ss";   

    // const handleDateChange = (date: Date | null) => {
    //   if (!date) return;

    //   const start = startOfDay(date).getTime();
    //   const end = endOfDay(date).getTime();

    //   // ✅ update the timeline region to the whole selected day
    //   setTimelineRegion([start, end]);

    //   // ✅ move cursor to *just after* midnight to avoid bounce
    //   setCurrentTimestamp(start + 1);
    // };

    let startOfDayInZone:
      | ((date: number | Date, timeZone: string) => number)
      | undefined;
    let startOfTomorrowInZone:
      | ((date: number | Date, timeZone: string) => number)
      | undefined;

    startOfDayInZone = function (date: number | Date, timeZone: string) {
      const zoned = toZonedTime(date, timeZone);
      zoned.setHours(0, 0, 0, 0);
      return zoned.getTime();
    };

    startOfTomorrowInZone = function (date: number | Date, timeZone: string) {
      const zoned = toZonedTime(date, timeZone);
      zoned.setHours(0, 0, 0, 0);
      zoned.setDate(zoned.getDate() + 1);
      return zoned.getTime();
    };

    const globalTimelineStart = useMemo(() => {
      const start = parseTimelineValue(timeline[0]?.start);
      return !short && start ? startOfDay(start).getTime() : start;
    }, [timeline, short]);

    const globalTimelineEnd = !short
      ? startOfTomorrow().getTime()
      : parseTimelineValue(timeline[timeline.length - 1]?.end);

    // timeSpan uses 4 hours on mobile for long timeline
    const timeSpan = useMemo(
      () =>
        short
          ? minutesToMilliseconds(15)
          : isMobile
          ? hoursToMilliseconds(4)
          : hoursToMilliseconds(24),
      [short, isMobile]
    );

    // // helper: return 4-hour bucket that contains "now"
    // const getCurrent4hBucket = useCallback((): [number, number] => {
    //   const now = new Date();
    //   const bucketIndex = Math.floor(now.getHours() / 4); // 0..5
    //   const startHour = bucketIndex * 4; // 0,4,8,12,16,20
    //   const start = setHours(startOfDay(now), startHour).getTime();
    //   const endHour = startHour + 4;
    //   const end =
    //     endHour === 24
    //       ? startOfTomorrow().getTime()
    //       : setHours(startOfDay(now), endHour).getTime();
    //   return [start, end];
    // }, []);

    // helper for 4h bucket containing any timestamp
    const get4hBucketFor = useCallback((ts: number): [number, number] => {
      const date = new Date(ts);
      const bucketIndex = Math.floor(date.getHours() / 4); // 0..5
      const startHour = bucketIndex * 4;
      const start = setHours(startOfDay(date), startHour).getTime();
      const endHour = startHour + 4;
      const end =
        endHour === 24
          ? startOfTomorrow().getTime()
          : setHours(startOfDay(date), endHour).getTime();
      return [start, end];
    }, []);

    const initTimelineRange: [number, number] = useMemo(() => {
      if (archiveItem) {
        const clipEnd =
          globalTimelineEnd - globalTimelineStart > timeSpan
            ? globalTimelineStart + timeSpan
            : globalTimelineEnd;
        return [globalTimelineStart, clipEnd];
      }
      if (short) {
        return [Date.now() - timeSpan, Date.now()];
      }
      if (isMobile) {
        return get4hBucketFor(Date.now());
      }
      return [startOfToday().getTime(), startOfTomorrow().getTime()];
    }, [
      short,
      timeSpan,
      archiveItem,
      globalTimelineStart,
      globalTimelineEnd,
      isMobile,
      get4hBucketFor,
    ]);

    const [[timelineStart, timelineEnd], setTimelineRegion] =
      useState(initTimelineRange);
    const nFrame = useTimedRender(1000);

    const [clicked, setClicked] = useState(false);
    const [movingCursorPosition, setMovingCursorPosition] = useState<
      number | null
    >(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const cursorDraggingRef = useRef<boolean>(false);
    const cursorTimeLabelRef = useRef<HTMLDivElement>(null);

    const handleMouseDown = useCallback(() => {
      setClicked(true);
    }, []);

    const getCursorTime = useCallback(
      (e: MouseEvent, container: HTMLDivElement) => {
        const box = container.getBoundingClientRect();
        let cursorTime = mapNumber(
          e.clientX,
          box.left,
          box.left + box.width,
          timelineStart,
          timelineEnd
        );
        if (cursorTime < timelineStart) cursorTime = timelineStart;
        if (cursorTime > timelineEnd) cursorTime = timelineEnd;
        return cursorTime;
      },
      [timelineStart, timelineEnd]
    );
    
    const handleMouseUp = useCallback(
      (e: MouseEvent) => {
        if (clicked && containerRef.current) {
          const clickTime = getCursorTime(e, containerRef.current);
          // ✅ Normalize midnight edge-case
          const normalizedClickTime =
            clickTime === startOfDay(new Date(clickTime)).getTime()
              ? clickTime + 1
              : clickTime;
          console.log("🖱️ [Timeline] MouseUp at time:", new Date(normalizedClickTime).toISOString());

          onSeek?.(normalizedClickTime);

          if (isMobile) {
            setTimelineRegion(get4hBucketFor(clickTime));
          }
        }
        setClicked(false);
        cursorDraggingRef.current = false;
        setMovingCursorPosition(null);
      },
      [clicked, onSeek, getCursorTime, isMobile, short, get4hBucketFor]
    );

    // const handleMouseUp = useCallback(
    //   (e: MouseEvent) => {
    //     if (clicked && containerRef.current) {
    //       const clickTime = getCursorTime(e, containerRef.current);
    //       onSeek?.(clickTime);

    //       if (isMobile) {
    //         // ensure visible window contains the seek time
    //         setTimelineRegion(get4hBucketFor(clickTime));
    //       }
    //     }
    //     setClicked(false);
    //     cursorDraggingRef.current = false;
    //     setMovingCursorPosition(null);
    //   },
    //   [clicked, onSeek, getCursorTime, isMobile, short, get4hBucketFor]
    // );

    useEffect(() => {
      document.body.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.body.removeEventListener("mouseup", handleMouseUp);
      };
    }, [handleMouseUp]);

    const cursorLeft = useMemo(() => {
      if (!containerRef.current) return null;
      const valueToMap =
        cursorDraggingRef.current && movingCursorPosition
          ? movingCursorPosition
          : seekPosRef.current;
      return mapNumber(
        valueToMap!,
        timelineStart,
        timelineEnd,
        0,
        containerRef.current.offsetWidth
      );
    }, [
      seekPosRef.current,
      timelineStart,
      timelineEnd,
      movingCursorPosition,
      nFrame,
    ]);

    const valueToMap =
      cursorDraggingRef.current && movingCursorPosition
        ? movingCursorPosition
        : seekPosRef.current;

    useEffect(() => {
      if (
        !cursorTimeLabelRef.current ||
        !containerRef.current ||
        cursorLeft === null
      ) {
        return;
      }
      const leftEdge = cursorLeft <= TIME_LABEL_WIDTH / 2;
      const rightEdge =
        containerRef.current.offsetWidth - cursorLeft <= TIME_LABEL_WIDTH / 2;
      if (leftEdge) {
        cursorTimeLabelRef.current.style.left = "0px";
      } else if (rightEdge) {
        cursorTimeLabelRef.current.style.left = `${
          containerRef.current.offsetWidth - TIME_LABEL_WIDTH
        }px`;
      } else {
        cursorTimeLabelRef.current.style.left = `${
          cursorLeft - TIME_LABEL_WIDTH / 2
        }px`;
      }
    }, [cursorLeft]);

    useEffect(() => {
      let intervalId;
      if (!short || !cursorDraggingRef.current || !movingCursorPosition) {
        if (intervalId) {
          clearInterval(intervalId);
        }
        return;
      }
      const leftShift =
        movingCursorPosition <= timelineStart + SHORT_TIMELINE_SHIFT_MS &&
        globalTimelineStart < timelineStart;

      const rightShift =
        movingCursorPosition >= timelineEnd - SHORT_TIMELINE_SHIFT_MS &&
        globalTimelineEnd > timelineEnd;

      if (!leftShift && !rightShift) {
        if (intervalId) {
          clearInterval(intervalId);
        }
        return;
      }
      const msDiff = leftShift ? -1000 : 1000;

      intervalId = setInterval(() => {
        setTimelineRegion(([start, end]) => {
          return [start + msDiff, end + msDiff];
        });
        setMovingCursorPosition((pos) => {
          if (pos) {
            return pos + msDiff;
          }
          return pos;
        });
      }, 10);
      return () => clearInterval(intervalId);
    }, [short, cursorDraggingRef.current, timelineStart, timelineEnd]);

    const handleCursorMouseDown = useCallback((e) => {
      e.stopPropagation();
      cursorDraggingRef.current = true;
      setClicked(true);
    }, []);

    const shiftTimelineByHours = useCallback((hours: number) => {
      const ms = hoursToMilliseconds(hours);
      setTimelineRegion(([start, end]) => [start + ms, end + ms]);
      onSetManual?.(true);
    }, []);

    const arrowsShown = useMemo(() => {
      return !short && globalTimelineEnd - globalTimelineStart > timeSpan;
    }, [short, globalTimelineStart, globalTimelineEnd, timeSpan]);

    const showNextDayTimeline = useCallback(() => {
      if (timelineEnd === globalTimelineEnd) return;
      const start = timelineStart + timeSpan;
      const end = timelineEnd + timeSpan;
      setTimelineRegion([start + 1, end]);
      // fetchTimelineTransactions({ siteId: cameraId, startTime: start, endTime: end });
      onSetManual?.(true);
    }, [
      timelineStart,
      timelineEnd,
      timeSpan,
      globalTimelineEnd,
      cameraId,
      onSetManual,
    ]);

    const showPrevDayTimeline = useCallback(() => {
      if (timelineStart === globalTimelineStart) return;
      const start = timelineStart - timeSpan;
      const end = timelineEnd - timeSpan;
      setTimelineRegion([start + 1, end]);
      // fetchTimelineTransactions({ siteId: cameraId, startTime: start, endTime: end });
      onSetManual?.(true);
    }, [
      timelineStart,
      timelineEnd,
      timeSpan,
      globalTimelineStart,
      cameraId,
      onSetManual,
    ]);

    const handleMouseMove = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (!containerRef.current) return;
        const time = getCursorTime(e.nativeEvent, containerRef.current);
        const box = containerRef.current.getBoundingClientRect();
        const left = e.clientX - box.left;
        setHoverTime(time);
        setHoverPosition(left);
      },
      [getCursorTime]
    );

    const handleMouseLeave = useCallback(() => {
      setHoverTime(null);
      setHoverPosition(null);
    }, []);

    useEffect(() => {
      if (containerRef.current && cursorLeft !== null) {
        const container = containerRef.current;
        const scrollTo = cursorLeft - container.offsetWidth / 2;
        container.scrollLeft = Math.max(0, scrollTo);
      }
    }, [cursorLeft]);

    //  useEffect(() => {
    //         const container = containerRef.current;
    //         if (container) {
    //             container.addEventListener("mousemove", handleMouseMove);
    //         }
    //         return () => {
    //             if (container) {
    //                 container.removeEventListener("mousemove", handleMouseMove);
    //             }
    //         };
    //     }, [handleMouseMove]);

    // useEffect(() => {
    //   if (posOn && cameraId && currentSeekTime) {
    //     console.log("🎯 [Timeline] Triggering transaction fetch:", {
    //       posOn,
    //       cameraId,
    //       currentSeekTime: new Date(currentSeekTime).toISOString(),
    //       fetchTimelineTransactions: !!fetchTimelineTransactions
    //     });
        
    //     // ✅ Ensure we're calling with proper parameters
    //     if (fetchTimelineTransactions) {
    //       // Generate time range for the day
    //       const seekDate = new Date(currentSeekTime);
    //       const dayStart = new Date(seekDate);
    //       dayStart.setHours(0, 0, 0, 0);
    //       const dayEnd = new Date(seekDate);
    //       dayEnd.setHours(23, 59, 59, 999);
          
    //       const params = {
    //         siteId: cameraId,
    //         startTime: dayStart.getTime(),
    //         endTime: dayEnd.getTime()
    //       };
          
    //       console.log("🌐 [Timeline] Calling fetchTimelineTransactions with:", params);
    //       fetchTimelineTransactions(params);
    //     }
    //   } else {
    //     console.log("🚫 [Timeline] Not triggering transaction fetch:", {
    //       posOn,
    //       cameraId: !!cameraId,
    //       currentSeekTime: !!currentSeekTime
    //     });
    //   }
    // }, [posOn, cameraId, currentSeekTime, fetchTimelineTransactions]);

    return (
      <div className={styles.timelineWithArrows}>
        {arrowsShown && !isMobile && (
          <KeyboardDoubleArrowLeftIcon
            className={clsx(styles.arrowWrapper, {
              [styles.arrowDisabled]: timelineStart === globalTimelineStart,
            })}
            onClick={showPrevDayTimeline}
          />
        )}

        {isMobile && (
          <KeyboardDoubleArrowLeftIcon
            className={clsx(styles.arrowWrapper, {
              [styles.arrowDisabled]: timelineStart <= startOfToday().getTime(),
            })}
            onClick={() => {
              if (timelineStart > startOfToday().getTime()) {
                shiftTimelineByHours(-4);
              }
            }}
          />
        )}

        {/* {isMobile && (
          <KeyboardDoubleArrowLeftIcon
            className={clsx(styles.arrowWrapper, {
              [styles.arrowDisabled]: timelineStart === globalTimelineStart,
            })}
            onClick={() => shiftTimelineByHours(-4)}
          />
        )} */}

        <div
          className={clsx(
            styles.container,
            clicked && styles.dragging,
            styles.saveMode,
            !short && styles.bottomTimeline
          )}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          ref={containerRef}
        >
          <Timeline
            timeline={timeline}
            timeZone={timeZone}
            seekPos={currentSeekTime || null}
            short={short}
            manualPosition={manualPosition}
            onSetManual={onSetManual}
            timelineStart={timelineStart}
            timelineEnd={timelineEnd}
            setTimelineRegion={setTimelineRegion}
            timeSpan={timeSpan}
            globalTimelineEnd={globalTimelineEnd}
            globalTimelineStart={globalTimelineStart}
            containerRef={containerRef}
            clicked={clicked}
            setClicked={setClicked}
            playbackOn={playbackOn}
            cameraId={cameraId}
            posOn={posOn}
            cursorDraggingRef={cursorDraggingRef}
            setMovingCursorPosition={setMovingCursorPosition}
            getCursorTime={getCursorTime}
            fetchTimelineTransactions={fetchTimelineTransactions}
            timelineTransactions={timelineTransactions}
            resetTimelineTransactions={resetTimelineTransactions}
          />

          {/* Hover Time Label */}
          {hoverPosition !== null && hoverTime !== null && (
            <div
              className={styles.hoverTimeLabel}
              style={{ left: `${hoverPosition - TIME_LABEL_WIDTH / 2}px` }}
            >
              {formatInTimeZone(hoverTime, timeZone, TOOLTIP_FORMAT)}
            </div>
          )}

          {/* Cursor Time Label */}
          {cursorLeft !== null && seekPosRef.current !== null && (
            <div className={styles.cursorTimeLabel} ref={cursorTimeLabelRef}>
              {formatInTimeZone(
                movingCursorPosition || currentSeekTime,
                timeZone,TOOLTIP_FORMAT
              )}
            </div>
          )}

          {/* Cursor and Draggable Area */}
          {cursorLeft !== null && (
            <>
              <div
                className={clsx(styles.cursor)}
                style={{ left: `${cursorLeft}px` }}
              ></div>
              <div
                className={clsx(styles.cursorDragArea)}
                onMouseDown={handleCursorMouseDown}
                style={{ left: `${cursorLeft}px` }}
              />
            </>
          )}
        </div>

        {isMobile && (
          <KeyboardDoubleArrowRightIcon
            className={clsx(styles.arrowWrapper, {
              [styles.arrowDisabled]: timelineEnd >= Date.now(),
            })}
            onClick={() => {
              if (timelineEnd < Date.now()) {
                shiftTimelineByHours(4);
              }
            }}
          />
        )}

        {arrowsShown && !isMobile && (
          <KeyboardDoubleArrowRightIcon
            className={clsx(styles.arrowWrapper, {
              [styles.arrowDisabled]: timelineEnd === globalTimelineEnd,
            })}
            onClick={showNextDayTimeline}
          />
        )}
      </div>
    );
  }
);